import { format } from "date-fns";
import { de } from "date-fns/locale";
import ExcelJS from "exceljs";

import DownloadExcelButton from "~/components/buttons/DownloadExcelButton";
import { DEFAULT_EXCEL_TABLE_STYLE } from "~/constants/excelConsts.ts";
import type {
	BenefitsReportTableData,
} from "~/modules/humanResources/components/StaffBenefitsReportView/benefitsReportTableTypes.ts";
import {
	getBenefitReportExcelColumnDefs,
} from "~/modules/humanResources/components/StaffBenefitsReportView/components/DownloadBenefitsReportExcelButton/monthlyCompensationReportExcelDef.tsx";


function DownloadBenefitsReportExcelButton({ benefitColNames, data, downloadDisabled }: {
	benefitColNames: string[];
	data: BenefitsReportTableData[],
	downloadDisabled: boolean,
}) {
	const generateExcel = async () => {
		const workBookTitle = "Benefits-Report " + format(new Date(), "dd.MM. yyyy", { locale: de });
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet(workBookTitle);

		const columnDefs = getBenefitReportExcelColumnDefs(benefitColNames.sort());

		// Convert data for table
		const tableData = data.map(row =>
			columnDefs.map(col => {

				if (col.accessor === "benefitsTotalCents") {
					return ""; // Empty value for total column
				}
				const value = row[col.accessor as keyof BenefitsReportTableData];
				if (!value) return 0;
				if (col.type === "currency" && typeof value === "number") {
					return value / 100;
				}
				return value;
			})
		);

		// Create table
		const headerRowNumber = 1; // Since you start at row 1
		const tableEndRowNumber = headerRowNumber + data.length;
		const tableEndColumnLetter = String.fromCharCode(64 + columnDefs.length);
		const tableRef = `A${headerRowNumber}:${tableEndColumnLetter}${tableEndRowNumber}`;

		worksheet.addTable({
			name: 'BenefitsReportTable',
			ref: tableRef, // Use calculated reference instead of hardcoded
			headerRow: true,
			totalsRow: false,
			style: {
				theme: DEFAULT_EXCEL_TABLE_STYLE,
				showRowStripes: true,
			},
			columns: columnDefs.map(col => ({
				name: col.header,
				filterButton: true,
			})),
			rows: tableData,
		});

		// Apply cell formatting to data rows
		for (let rowIndex = 2; rowIndex <= data.length + 1; rowIndex++) {
			columnDefs.forEach((col, colIndex) => {
				const cell = worksheet.getCell(rowIndex, colIndex + 1);

				switch (col.type) {
					case "currency":
						cell.numFmt = "#,##0.00 \"€\"";
						break;
				}

				cell.alignment = { vertical: "top", wrapText: true };
			});
		}

		// Apply formula for benefitsTotalCents
		const totalPaymentCol = columnDefs.findIndex(col => col.accessor === "benefitsTotalCents");
		if (totalPaymentCol !== -1) {
			for (let rowIndex = 2; rowIndex <= data.length + 1; rowIndex++) {
				const cell = worksheet.getCell(rowIndex, totalPaymentCol + 1);
				// Sum all columns from B (column 2) to the column before total
				const startCol = String.fromCharCode(66); // B
				const endCol = String.fromCharCode(65 + totalPaymentCol - 1); // Column before total
				cell.value = {
					formula: `SUM(${startCol}${rowIndex}:${endCol}${rowIndex})`,
				};
				cell.numFmt = "#,##0.00 \"€\"";
			}
		}

		// Auto-fit columns
		worksheet.columns.forEach((column) => {
			let maxLength = 0;

			// @ts-expect-error - TS doesn't know about the includeEmpty option
			column.eachCell({ includeEmpty: true }, (cell) => {
				let cellLength:number;
				if (cell.value) {
					cellLength = cell.value.toString().length;
				} else {
					cellLength = 10; // Default length for empty cells
				}
				if (cellLength > maxLength) {
					maxLength = cellLength;
				}
			});

			column.width = Math.min(maxLength + 2, 30); // Set width to content, but max 30
		});

		// Ensure all columns are visible
		worksheet.views = [
			{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2" },
		];

		// Generate and download the Excel file
		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = workBookTitle + ".xlsx";
		a.click();
		window.URL.revokeObjectURL(url);
	};

	return <DownloadExcelButton disabled={downloadDisabled}
								buttonText="Excel-Report herunterladen"
								onClick={generateExcel} />;
}

export default DownloadBenefitsReportExcelButton;